import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { StaffSpeciality } from '@/types/settings-api';

/**
 * Speciality master search (SNOMED codes) — PROD-761 commit 8.
 *
 * GET /api/Master/PracticeCodeList?searchQuery={q}&system={specialitySystem}
 *
 * Source for the Professional Details **Specialities** picker and the
 * Staff Account speciality picker (PROD-788 will share this hook).
 *
 * Flutter parity (field_and_label.dart:2540-2548):
 *   - `system` query param is read from
 *     `userData.resourceBasedIntegration.specialitySystem` at runtime,
 *     i.e. per-org configuration. For demo / dev org the field is unset
 *     and the param ships as a literal empty string.
 *   - We expose `system` as an optional caller parameter, default `''`,
 *     so future orgs that DO populate it can wire the resolved value in
 *     from session state without changing this hook. Don't hardcode
 *     a SNOMED IRI here — that bakes in the demo-org assumption.
 *
 * Response shape verified against live dev API 2026-05-14 (cURL captured
 * with searchQuery=gen against org demo, system empty) — exact same
 * envelope as `useProcedureCodesSearch`:
 *
 *   [{
 *     specialitySkillId: "3844222d-…",
 *     code: {
 *       code: "394576009",
 *       displayName: "Surgical-Accident & emergency",
 *       system: "http://snomed.info/sct"
 *     }
 *   }, ...]
 *
 * So consumers can append a master result directly to
 * `ProfessionalDetails.speciality` (which is `StaffSpeciality[]`) — no
 * adapter needed, mirroring the Skills typeahead in commit 6.
 *
 * Caller debounces its `searchQuery` input (250ms — matches Languages
 * (#116) and Skills (#117)). Empty query short-circuits to avoid
 * hitting the API on mount; the catalogue is server-side filtered
 * but the FE shouldn't load it untyped.
 */
export function useSpecialityCodesSearch(searchQuery: string, system = '') {
  const trimmed = searchQuery.trim();
  return useQuery({
    queryKey: ['settings', 'master', 'speciality-codes', trimmed, system],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: trimmed, system });
      return get<StaffSpeciality[]>(
        `/api/Master/PracticeCodeList?${qs.toString()}`,
        { signal },
      );
    },
    enabled: trimmed.length > 0,
    staleTime: 60_000,
  });
}
